import React, { FC, useCallback, useMemo, useState } from "react";
import { Box, Grid2 as Grid } from "@mui/material";
import CharacterItem from "@components/CharactersForm/CharacterItem";
import { Character } from "@models/character";
import { Person } from "@models/person";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import PersonSelectDialog from "@components/CharactersForm/PersonSelectDialog";
import { useGetPersonsQuery } from "@store/personApi";
import {
  useDeleteCharacterMutation,
  useUpdateCharacterMutation,
} from "@store/characterApi";
import { setMessage } from "@store/snackbarSlice";
import { useDispatch } from "react-redux";
import ConfirmDialog from "@components/ConfirmDialog/ConfirmDialog";
import CharacterFacesDrawer from "@components/Home/Faces/CharacterFacesDrawer";

const SectionTitle = styled.div`
  font-size: 14px;
  line-height: 22px;
  font-weight: 700;
  color: #171a1fff;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 10px;
`;

const Section: FC<{
  onDelete?: (character: Character) => void;
  onChange: (character: Character) => void;
  onCheck?: (character: Character) => void;
  onSelect?: (character: Character) => void;
  title: string;
  characters: Character[];
  checkedCharacters?: Set<Character>;
}> = ({
  onDelete,
  onChange,
  characters,
  title,
  onCheck,
  onSelect,
  checkedCharacters,
}) => {
  return (
    <Box>
      <SectionTitle>{title}</SectionTitle>
      <Box display="flex" gap="24px" flexWrap="wrap">
        {characters?.length
          ? characters.map((character) => (
              <CharacterItem
                key={character.id}
                person={character.person}
                character={character}
                onRemove={onDelete && (() => onDelete(character))}
                onChange={() => onChange(character)}
                onCheck={onCheck && (() => onCheck(character))}
                onSelect={onSelect && (() => onSelect(character))}
                checked={checkedCharacters?.has(character)}
              />
            ))
          : "-"}
      </Box>
    </Box>
  );
};

export interface CharactersFormProps {
  characters: Character[];
  patientId: string;
  onCheck?: (character: Character) => void;
  checkedCharacters?: Set<Character>;
  enableViewDrawer?: boolean;
}

const CharactersForm: FC<CharactersFormProps> = ({
  characters,
  patientId,
  onCheck,
  checkedCharacters,
  enableViewDrawer,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(
    null,
  );
  const [facesDrawerCharacter, setFacesDrawerCharacter] =
    useState<Character | null>(null);
  const { data: personsData } = useGetPersonsQuery(patientId);
  const persons = useMemo(() => personsData?.data || [], [personsData]);
  const [update, { isLoading: isUpdating }] = useUpdateCharacterMutation();
  const [characterDelete, { isLoading: isDeleting }] =
    useDeleteCharacterMutation();
  const [confirmCharacterDelete, setConfirmCharacterDelete] =
    useState<Character | null>(null);
  const [confirmMerge, setConfirmMerge] = useState<{
    person: Person;
    character: Character;
  } | null>(null);

  const deleteCharacter = async (character: Character) => {
    const { error } = await characterDelete(character.id);

    if (error) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
    setConfirmCharacterDelete(null);
    setFacesDrawerCharacter(null);
  };

  const saveCharacterPerson = useCallback(
    async (character: Character, personId: string | null, merge: boolean) => {
      const { error } = await update({
        id: character.id,
        personId,
        merge,
      });

      if (error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        setFacesDrawerCharacter(null);
      }
    },
    [dispatch, update, t],
  );

  const characterGroups = useMemo(() => {
    const groups: { pending: Character[]; tagged: Character[] } = {
      pending: [],
      tagged: [],
    };
    characters.forEach((character) => {
      groups[character.person ? "tagged" : "pending"].push(character);
    });
    return groups;
  }, [characters]);

  const openPersonSelectDialog = (character: Character) => {
    setSelectedCharacter(character);
  };

  const closePersonSelectDialog = () => {
    setSelectedCharacter(null);
  };

  const onPersonSelected = useCallback(
    async (person: Person) => {
      if (!selectedCharacter) {
        return;
      }
      if (person.characterId) {
        setSelectedCharacter(null);
        setConfirmMerge({ person, character: selectedCharacter });
        return;
      }
      await saveCharacterPerson(selectedCharacter, person.id, false);
      setSelectedCharacter(null);
    },
    [selectedCharacter, saveCharacterPerson],
  );

  const onConfirmMerge = useCallback(async () => {
    if (!confirmMerge) {
      return;
    }
    const { person, character } = confirmMerge;
    await saveCharacterPerson(character, person.id, true);
    setSelectedCharacter(null);
    setConfirmMerge(null);
  }, [confirmMerge, saveCharacterPerson]);

  const onCancelMerge = useCallback(async () => {
    setConfirmMerge(null);
    setSelectedCharacter(null);
  }, []);

  return (
    <Grid container rowSpacing={4}>
      {characterGroups.pending.length ? (
        <Grid size={12}>
          <Section
            characters={characterGroups.pending}
            onDelete={!onCheck ? setConfirmCharacterDelete : undefined}
            onCheck={onCheck}
            onSelect={(character) =>
              enableViewDrawer && setFacesDrawerCharacter(character)
            }
            onChange={openPersonSelectDialog}
            title={t("characters.pending")}
            checkedCharacters={checkedCharacters}
          />
        </Grid>
      ) : (
        ""
      )}

      {characterGroups.tagged.length ? (
        <Grid size={12}>
          <Section
            characters={characterGroups.tagged}
            onDelete={!onCheck ? setConfirmCharacterDelete : undefined}
            onCheck={onCheck}
            onSelect={(character) =>
              enableViewDrawer && setFacesDrawerCharacter(character)
            }
            onChange={openPersonSelectDialog}
            title={t("characters.tagged")}
            checkedCharacters={checkedCharacters}
          />
        </Grid>
      ) : (
        ""
      )}

      <ConfirmDialog
        processing={isDeleting}
        open={!!confirmCharacterDelete}
        onCancel={() => setConfirmCharacterDelete(null)}
        onConfirm={() => deleteCharacter(confirmCharacterDelete!)}
      />

      <PersonSelectDialog
        onClose={closePersonSelectDialog}
        onPersonSelected={onPersonSelected}
        open={!!selectedCharacter}
        persons={persons}
        characters={characters}
      />

      <ConfirmDialog
        processing={isUpdating}
        open={!!confirmMerge}
        onCancel={onCancelMerge}
        onConfirm={onConfirmMerge}
        content={t("characters.confirmMergeDialog.content")}
      />

      <CharacterFacesDrawer
        onClose={() => setFacesDrawerCharacter(null)}
        character={facesDrawerCharacter}
        onChange={() => setSelectedCharacter(facesDrawerCharacter)}
        onDelete={() => setConfirmCharacterDelete(facesDrawerCharacter)}
      />
    </Grid>
  );
};

export default CharactersForm;
